import type { ColumnDef } from '@tanstack/react-table';
import {
  AREA_COLUMN_ID,
  createSerialColumn,
  DrillDownCountButton,
  getAreaColumn,
  MemberNameCell,
  WrappingCellText,
  type GroupedColumn,
} from '@/components/common/DataTable';
import { EmptyValue } from '@/components/common/EmptyValue';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { formatCount, formatNumber } from '@/utils/number';
import { LEAVE_REPORT_DATE_COUNT_KEY } from '../constants/leaveReport.constants';
import type { LeaveReportDateCategory, LeaveReportItem } from '../types/leaveReport.types';

const COMPANY_COLUMN_ID = 'company';
const DEPARTMENT_COLUMN_ID = 'department';
const REPORTING_TO_COLUMN_ID = 'reportingTo';
const EMPLOYEE_COLUMN_ID = 'employee';
const AREA_KEY_SEPARATOR = '|';

const DATE_UNIT = 'date';

export const LEAVE_REPORT_GROUPED_COLUMNS: GroupedColumn<LeaveReportItem>[] = [
  { columnId: COMPANY_COLUMN_ID, getGroupKey: (row) => row.company },
  { columnId: DEPARTMENT_COLUMN_ID, getGroupKey: (row) => row.department },
  { columnId: REPORTING_TO_COLUMN_ID, getGroupKey: (row) => row.reportingToId },
  {
    columnId: AREA_COLUMN_ID,
    getGroupKey: (row) => [row.thana, row.district, row.division].join(AREA_KEY_SEPARATOR),
  },
];

interface DateCountColumn {
  category: LeaveReportDateCategory;
  header: string;
}

const DATE_COUNT_COLUMNS: DateCountColumn[] = [
  { category: 'absent', header: 'Absent (Days)' },
  { category: 'weekend', header: 'Weekends' },
  { category: 'holiday', header: 'Govt Holiday' },
  { category: 'total', header: 'Total Days' },
  { category: 'applied', header: 'Applied Days' },
  { category: 'unapplied', header: 'Unapplied Days' },
];

export function getEmployeeName(row: LeaveReportItem): string {
  return row.employeeName ?? EMPTY_VALUE;
}

export function getDateCategoryCount(
  row: LeaveReportItem,
  category: LeaveReportDateCategory,
): number {
  return row[LEAVE_REPORT_DATE_COUNT_KEY[category]];
}

function renderWrappingText(text: string | null) {
  return text ? <WrappingCellText>{text}</WrappingCellText> : <EmptyValue />;
}

function renderReportingTo(row: LeaveReportItem) {
  if (!row.reportingToName) return <EmptyValue />;
  return (
    <MemberNameCell
      member={{ name: row.reportingToName, hrEmpId: row.reportingToHrEmpId, mobile: null }}
    />
  );
}

function toDateCountColumn(
  { category, header }: DateCountColumn,
  onOpenDates: (row: LeaveReportItem, category: LeaveReportDateCategory) => void,
): ColumnDef<LeaveReportItem, unknown> {
  return {
    id: category,
    header,
    cell: ({ row }) => {
      const count = getDateCategoryCount(row.original, category);
      return (
        <DrillDownCountButton
          count={count}
          hint={`View ${formatCount(count, DATE_UNIT)}`}
          ariaLabel={`${header} of ${getEmployeeName(row.original)}: view ${formatCount(count, DATE_UNIT)}`}
          onOpen={() => onOpenDates(row.original, category)}
        />
      );
    },
  };
}

export function getLeaveReportColumns(
  onOpenDates: (row: LeaveReportItem, category: LeaveReportDateCategory) => void,
  searchText: string | undefined,
): ColumnDef<LeaveReportItem, unknown>[] {
  return [
    createSerialColumn<LeaveReportItem>(),
    {
      id: COMPANY_COLUMN_ID,
      header: 'Company',
      cell: ({ row }) => renderWrappingText(row.original.company),
    },
    {
      id: DEPARTMENT_COLUMN_ID,
      header: 'Department',
      cell: ({ row }) => renderWrappingText(row.original.department),
    },
    {
      id: REPORTING_TO_COLUMN_ID,
      header: 'Reporting To',
      cell: ({ row }) => renderReportingTo(row.original),
    },
    getAreaColumn<LeaveReportItem>(
      (row) => ({
        divisionName: row.division,
        districtName: row.district,
        thanaName: row.thana,
      }),
      { isCommaSeparated: true },
    ),
    {
      id: EMPLOYEE_COLUMN_ID,
      header: 'Employee Name',
      cell: ({ row }) => (
        <MemberNameCell
          member={{
            name: getEmployeeName(row.original),
            hrEmpId: row.original.hrEmpId,
            mobile: null,
          }}
          searchText={searchText}
        />
      ),
    },
    {
      id: 'present',
      header: 'Present (Days)',
      cell: ({ row }) => (
        <span className="tabular-nums">{formatNumber(row.original.presentDays)}</span>
      ),
    },
    ...DATE_COUNT_COLUMNS.map((column) => toDateCountColumn(column, onOpenDates)),
  ];
}
